/**
 * Route arrows (order 763, presentation only; docs/UI.md "Route arrows"): chevrons painted on the road
 * along the shortest way to the route goal (`onboarding.goal`, routeGoal.ts) — ONLY the running job's next
 * stop (red, like its marker) or the place the player chose (plum, like the mark's flag); never a
 * suggestion, a shop or an event. A couple of hundred metres ahead of the car (less on foot, on the
 * pavement). Like a lollipop man's chalk on the tarmac: half worn, readable from the high camera.
 *
 * One instanced mesh of at most MAX_CHEVRONS flat chevrons; the path is replanned about once a second
 * (or at once when the player strays off it or the target moves) by an allocation-free Dijkstra over
 * the road graph (route.ts). Hidden in cutscenes (letterbox, a hidden HUD, a camera beat), behind
 * menus, in the passenger seat, close to the target, and when Settings → Route arrows is OFF. Online
 * too: it only reads the map markers.
 */
import * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { DRIVE_CHEVRONS, WALK_CHEVRONS, RoutePlanner, makeRoutePath, placeChevrons, projectOnPath, type RoutePath } from './route';

const MAX_CHEVRONS = 32;
/** Replan this often (s), or sooner when off the path by OFF_PATH m (at most every MIN_REPLAN s). */
const REPLAN_EVERY = 1.2;
const OFF_PATH = 9;
const MIN_REPLAN = 0.3;
/** The target moved this far (m): a new route at once. */
const TARGET_MOVED = 4;
/** Nothing to paint this close to the target (m): the beacon says it. */
const NEAR_TARGET = 14;
const DRIVE_Y = 0.03;
const WALK_Y = 0.16;
const DRIVE_SCALE = 1;
const WALK_SCALE = 0.55;
/** The scheme's colours, a shade lighter so they read on dark tarmac: the job's red, the mark's plum. */
const JOB_COLOR = 0xe0533a;
const MARK_COLOR = 0xc8a2ea;
const OPACITY = 0.66;

/**
 * The planned route for the maps (src/ui/minimap.ts draws it as a line on the road): the path while arrows are
 * shown, and whose colour it is — null when there is nothing to lead to.
 */
export interface RouteLineService {
  readonly path: RoutePath | null;
  readonly kind: '' | 'job' | 'mark';
}

declare module '../core/services' {
  interface ServiceMap {
    route: RouteLineService;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Route arrows read-out: shown, target, path length, where the player is along it, chevrons drawn. */
    routeState(): Record<string, unknown>;
  }
}

/** A flat chevron pointing +Z, about 1.5 m across, on the XZ plane. */
function chevronGeometry(): THREE.BufferGeometry {
  const w = 0.75;
  const tip = 0.5;
  const back = -0.25;
  const th = 0.34;
  // tip, tip inner, right, right inner, left, left inner
  const p = [0, 0, tip, 0, 0, tip - th, w, 0, back, w, 0, back - th, -w, 0, back, -w, 0, back - th];
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(p, 3));
  g.setIndex([0, 2, 3, 0, 3, 1, 0, 1, 5, 0, 5, 4]);
  g.computeVertexNormals();
  return g;
}

export function createRouteArrowsSystem(): System {
  let ctx: GameContext;
  let planner: RoutePlanner | null = null;
  let path: RoutePath | null = null;
  let mesh: THREE.InstancedMesh | null = null;
  const chevrons = new Float32Array(MAX_CHEVRONS * 4);
  const proj = { along: 0, off: Infinity };
  const target = { x: 0, z: 0, ok: false, kind: '' as '' | 'job' | 'mark' };
  let planned = { x: 0, z: 0, walk: false, ok: false };
  let clock = 0;
  let replanAt = 0;
  let lastPlanAt = -99;
  let shown = 0;
  let enabled = true;
  const m4 = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scl = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  const col = new THREE.Color();
  const offs: (() => void)[] = [];

  /** The route goal (the onboarding system works it out for the pointer too, so the two always agree). */
  function pickTarget(): boolean {
    const g = ctx.services.tryGet('onboarding')?.goal;
    if (!g) return false;
    if (g.kind !== target.kind && mesh) (mesh.material as THREE.MeshBasicMaterial).color.setHex(g.kind === 'mark' ? MARK_COLOR : JOB_COLOR);
    target.x = g.x;
    target.z = g.z;
    target.kind = g.kind;
    return true;
  }

  function hide(): void {
    if (mesh && shown > 0) {
      mesh.count = 0;
      mesh.visible = false;
    }
    shown = 0;
  }

  function visibleNow(): boolean {
    if (!enabled || ctx.state.mode !== 'playing') return false;
    const ui = ctx.services.tryGet('ui');
    if (!ui || !ui.free || ui.cinematic || ui.screen !== 'hud') return false;
    if (ctx.services.tryGet('cameraRig')?.isCinematic) return false;
    if (ctx.services.tryGet('onboarding')?.introOpen) return false;
    const p = ctx.services.tryGet('player');
    return !!p && (p.mode === 'foot' || p.mode === 'driving');
  }

  return {
    id: 'route-arrows',
    order: 763,
    init(c) {
      ctx = c;
      c.services.provide('route', {
        get path() {
          return shown > 0 && planned.ok ? path : null;
        },
        get kind() {
          return shown > 0 && target.ok ? target.kind : '';
        },
      });
      c.hooks.register('routeState', () => ({
        enabled,
        shown,
        target: target.ok ? { x: +target.x.toFixed(1), z: +target.z.toFixed(1), kind: target.kind } : null,
        walk: path?.walk ?? null,
        length: path ? +path.length.toFixed(1) : 0,
        along: +proj.along.toFixed(1),
        off: Number.isFinite(proj.off) ? +proj.off.toFixed(1) : null,
        chevrons: Array.from({ length: Math.min(shown, 4) }, (_, i) => ({ x: +chevrons[i * 4]!.toFixed(1), z: +chevrons[i * 4 + 1]!.toFixed(1) })),
      }));
      offs.push(c.events.on('settingsChanged', (e) => (enabled = e.settings.routeArrows !== false)));
      if (!c.render) return;
      // Flat chevrons on the road: one pass draws the same pixels as three's back-then-front pair for a
      // transparent double-sided material (two draws, two program re-fetches; docs/PERF.md "Phone pass").
      const mat = new THREE.MeshBasicMaterial({ color: JOB_COLOR, transparent: true, opacity: OPACITY, depthWrite: false, side: THREE.DoubleSide, forceSinglePass: true, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
      // Instance colour .r is the chevron's fade (the far end of the window dies away).
      mat.onBeforeCompile = (sh) => {
        sh.fragmentShader = sh.fragmentShader.replace('#include <color_fragment>', '#if defined( USE_COLOR )\n  diffuseColor.a *= vColor.r;\n#endif');
      };
      mesh = new THREE.InstancedMesh(chevronGeometry(), mat, MAX_CHEVRONS);
      for (let i = 0; i < MAX_CHEVRONS; i++) mesh.setColorAt(i, col.setRGB(1, 1, 1));
      mesh.count = 0;
      mesh.visible = false;
      mesh.frustumCulled = false;
      mesh.renderOrder = 2;
      mesh.name = 'route-arrows';
      c.scene.add(mesh);
    },
    start() {
      const layout = ctx.services.tryGet('layout');
      if (layout && layout.roads.nodes.length > 1) {
        planner = new RoutePlanner(layout);
        path = makeRoutePath(planner.capacity);
      }
      const s = ctx.services.tryGet('save')?.settings;
      if (s) enabled = s.routeArrows !== false;
    },
    update(dt) {
      clock += dt;
      if (!mesh || !planner || !path) return;
      const player = ctx.services.tryGet('player');
      if (!visibleNow() || !player) {
        hide();
        return;
      }
      const px = player.pos.x;
      const pz = player.pos.z;
      target.ok = pickTarget();
      if (!target.ok) {
        planned.ok = false;
        hide();
        return;
      }
      const walk = player.mode === 'foot';
      if (planned.ok) projectOnPath(path, player.pos, proj);
      const moved = !planned.ok || walk !== planned.walk || Math.hypot(target.x - planned.x, target.z - planned.z) > TARGET_MOVED;
      const strayed = planned.ok && proj.off > OFF_PATH && clock - lastPlanAt > MIN_REPLAN;
      if (moved || strayed || clock >= replanAt) {
        const heading = walk ? null : (player.vehicle?.heading ?? player.heading);
        const ok = planner.plan(player.pos, target, walk, heading, path);
        planned = { x: target.x, z: target.z, walk, ok };
        lastPlanAt = clock;
        replanAt = clock + REPLAN_EVERY;
        if (ok) projectOnPath(path, player.pos, proj);
      }
      if (!planned.ok || Math.hypot(target.x - px, target.z - pz) < NEAR_TARGET || path.length - proj.along < NEAR_TARGET) {
        hide();
        return;
      }
      const n = placeChevrons(path, proj.along, walk ? WALK_CHEVRONS : DRIVE_CHEVRONS, chevrons, MAX_CHEVRONS);
      const y = walk ? WALK_Y : DRIVE_Y;
      const s = walk ? WALK_SCALE : DRIVE_SCALE;
      for (let i = 0; i < n; i++) {
        const j = i * 4;
        quat.setFromAxisAngle(up, chevrons[j + 2]!);
        m4.compose(pos.set(chevrons[j]!, y, chevrons[j + 1]!), quat, scl.set(s, 1, s));
        mesh.setMatrixAt(i, m4);
        mesh.setColorAt(i, col.setRGB(chevrons[j + 3]!, 1, 1));
      }
      mesh.count = n;
      mesh.visible = n > 0;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      shown = n;
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      if (mesh) {
        mesh.removeFromParent();
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
        mesh = null;
      }
    },
  };
}
